import React, { useMemo, useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { isCategoryAvailable } from '../../utils/workflowConditionUtils';

function flattenSearchResults(nodes, path = [], workflowContext = {}) {
  const results = [];
  nodes.forEach((node) => {
    const currentPath = [...path, node];
    if (node.type === 'field') {
      results.push({ node, path: currentPath });
      return;
    }
    if (node.children?.length) {
      results.push(...flattenSearchResults(node.children, currentPath, workflowContext));
    }
  });
  return results;
}

function findFieldById(nodes, fieldId) {
  for (const node of nodes) {
    if (node.type === 'field' && node.id === fieldId) return node;
    if (node.children) {
      const found = findFieldById(node.children, fieldId);
      if (found) return found;
    }
  }
  return null;
}

const ConditionFieldPicker = ({
  catalog,
  value,
  onChange,
  workflowContext = {},
  placeholder = 'Type to search',
  className,
  'data-testid': testId,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [path, setPath] = useState([]);
  const containerRef = useRef(null);

  const selectedField = useMemo(
    () => (value ? findFieldById(catalog, value) : null),
    [catalog, value],
  );

  const currentLevel = useMemo(() => {
    if (!path.length) return catalog;
    let nodes = catalog;
    for (const segment of path) {
      const next = nodes.find((n) => n.id === segment.id);
      nodes = next?.children || [];
    }
    return nodes;
  }, [catalog, path]);

  const searchResults = useMemo(() => {
    if (!search.trim()) return null;
    const q = search.trim().toLowerCase();
    return flattenSearchResults(catalog).filter(({ node, path: nodePath }) => {
      const labels = nodePath.map((p) => p.label).join(' ');
      return node.label.toLowerCase().includes(q) || labels.toLowerCase().includes(q);
    });
  }, [catalog, search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const breadcrumb = path.length
    ? ['All fields', ...path.map((p) => p.label)]
    : ['All fields'];

  const handleSelectField = (field) => {
    onChange(field.id, field);
    setOpen(false);
    setSearch('');
    setPath([]);
  };

  const renderCategoryItem = (node) => {
    const disabled = node.type === 'category' && !isCategoryAvailable(node, workflowContext);
    const reason = node.disabledReason;

    const button = (
      <button
        type="button"
        key={node.id}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (node.type === 'field') {
            handleSelectField(node);
          } else {
            setPath((prev) => [...prev, { id: node.id, label: node.label }]);
            setSearch('');
          }
        }}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
          disabled
            ? 'cursor-not-allowed text-slate-300'
            : 'text-slate-700 hover:bg-slate-50',
        )}
      >
        <span className="flex-1 truncate">{node.label}</span>
        {node.type === 'category' ? <ChevronRight size={14} className="text-slate-400 shrink-0" /> : null}
      </button>
    );

    if (disabled && reason) {
      return (
        <Tooltip key={node.id}>
          <TooltipTrigger asChild>
            <div>{button}</div>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs">
            {reason}
          </TooltipContent>
        </Tooltip>
      );
    }
    return button;
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div ref={containerRef} className={cn('relative', className)} data-testid={testId}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 text-sm',
            open && 'border-emerald-500 ring-1 ring-emerald-500',
          )}
        >
          <span className={cn('truncate', !selectedField && 'text-slate-400')}>
            {selectedField?.label || placeholder}
          </span>
          <ChevronRight size={14} className={cn('text-slate-400 transition-transform', open && 'rotate-90')} />
        </button>

        {open ? (
          <div className="absolute left-0 right-0 top-full z-[120] mt-1 max-h-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-100 p-2">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={placeholder}
                  className="h-8 pl-8 text-sm"
                  autoFocus
                />
              </div>
            </div>

            {!search.trim() ? (
              <div className="flex items-center gap-1 border-b border-slate-100 px-3 py-2 text-xs text-slate-500">
                {path.length ? (
                  <button
                    type="button"
                    onClick={() => setPath((prev) => prev.slice(0, -1))}
                    className="flex items-center gap-1 text-emerald-600 hover:underline"
                  >
                    <ChevronLeft size={12} />
                    {breadcrumb.slice(0, -1).join(' > ') || 'All fields'}
                  </button>
                ) : null}
                <span className="truncate font-semibold text-slate-700">
                  {path.length ? `> ${breadcrumb[breadcrumb.length - 1]}` : 'All fields'}
                </span>
              </div>
            ) : (
              <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">
                Search results
              </div>
            )}

            <div className="max-h-52 overflow-y-auto py-1">
              {searchResults
                ? searchResults.map(({ node, path: nodePath }) => (
                  <button
                    key={node.id}
                    type="button"
                    onClick={() => handleSelectField(node)}
                    className="flex w-full flex-col px-3 py-2 text-left hover:bg-slate-50"
                  >
                    <span className="text-sm text-slate-800">{node.label}</span>
                    <span className="truncate text-xs text-slate-400">
                      {nodePath.filter((p) => p.type === 'category').map((p) => p.label).join(' > ')}
                    </span>
                  </button>
                ))
                : currentLevel.map(renderCategoryItem)}
            </div>
          </div>
        ) : null}
      </div>
    </TooltipProvider>
  );
};

export default ConditionFieldPicker;
export { findFieldById };
